// src/services/api.js
import axios from 'axios';
import logger from '../utils/logger.js';

// Create and export a default axios instance
const baseURL = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';

const defaultInstance = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
defaultInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for common error handling
defaultInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    logger.group('API Error Debug', () => {
      logger.debug('Error config:', error.config);
      logger.debug('Error message:', error.message);
      logger.debug('Error code:', error.code);
      logger.debug('Request URL:', error.config?.url);
      logger.debug('Request method:', error.config?.method);
      logger.debug('Base URL:', error.config?.baseURL);
      logger.debug('Full URL:', error.config?.baseURL + error.config?.url);
    });
    
    if (!error.response) {
      logger.error('Network Error:', error.message);
    } else if (error.response.status === 401) {
      // Handle different types of 401 errors
      const errorCode = error.response.data?.code;
      const errorMessage = error.response.data?.error;
      
      if (errorCode === 'TOKEN_EXPIRED' || errorMessage === 'Token expired') {
        logger.debug('Token expired, clearing storage and redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to login page
        window.location.href = '/login';
      } else if (errorCode === 'INVALID_TOKEN' || errorMessage === 'Invalid token') {
        logger.debug('Invalid token, clearing storage');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API functions
export const loginUser = async (email, password) => {
  const requestData = { 
    email: email?.trim().toLowerCase(),
    password: password
  };
  
  if (!requestData.email) {
    throw new Error('Email is required');
  }
  
  if (!requestData.password) {
    throw new Error('Password is required');
  }
  
  try {
    logger.api('Making login request to:', `${baseURL}/auth/login`);
    const response = await defaultInstance.post('/auth/login', requestData);
    
    if (!response.data || !response.data.token) {
      throw new Error('Invalid response from server - no token received');
    }
    
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
      throw new Error(errorMessages);
    }
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await defaultInstance.get('/auth/me');
    
    if (!response.data || !response.data._id) {
      throw new Error('Invalid user data received');
    }
    
    const userData = {
      ...response.data,
      _id: response.data._id || response.data.id,
      profile: response.data.profile || {},
      subscription: response.data.subscription || {
        tier: 'basic',
        status: 'active',
        isFree: true
      }
    };
    
    return userData;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please login again.');
    }
    throw error;
  }
};

const isConsentAccepted = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

export const registerUser = async (userData) => {
  logger.api('Sending registration data:', { 
    firstName: userData.firstName, 
    lastName: userData.lastName, 
    email: userData.email 
  });
  
  const acceptTerms = isConsentAccepted(userData.acceptTerms);
  const acceptedTerms = acceptTerms || isConsentAccepted(userData.acceptedTerms);
  const acceptedPrivacy = acceptTerms || isConsentAccepted(userData.acceptedPrivacy);

  // Rebuild the body with consent flags included. Production currently posts
  // only firstName/lastName/email/password, which makes the API return 400.
  const requestData = {
    firstName: userData.firstName?.trim(),
    lastName: userData.lastName?.trim(),
    email: userData.email?.trim().toLowerCase(),
    password: userData.password,
    acceptTerms: acceptedTerms && acceptedPrivacy,
    acceptedTerms,
    acceptedPrivacy
  };
  
  // Validate required fields
  if (!requestData.firstName || !requestData.lastName) {
    throw new Error('First name and last name are required');
  }
  
  if (!requestData.email) {
    throw new Error('Email is required');
  }

  if (!requestData.acceptTerms || !requestData.acceptedTerms || !requestData.acceptedPrivacy) {
    throw new Error('You must accept the Terms and Conditions and Privacy Policy to create an account');
  }
  
  // Password validation is handled by backend - no frontend check needed
  // Frontend validation removed to avoid confusion with backend requirements
  
  logger.api('Sending formatted data:', { 
    firstName: requestData.firstName, 
    lastName: requestData.lastName, 
    email: requestData.email,
    acceptTerms: requestData.acceptTerms,
    acceptedTerms: requestData.acceptedTerms,
    acceptedPrivacy: requestData.acceptedPrivacy
  });
  
  try {
    const response = await defaultInstance.post('/auth/register', requestData);
    return response.data;
  } catch (error) {
    logger.error('Registration error:', error.message);
    // Handle validation errors - extract specific field errors
    // Backend returns { error: "Validation failed", details: [...] }
    if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
      const validationErrors = error.response.data.details
        .map(err => err.message || err.msg)
        .filter(Boolean)
        .join('. ');
      if (validationErrors) {
        throw new Error(validationErrors);
      }
    }
    // Also check for errors array (alternative format)
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const validationErrors = error.response.data.errors
        .map(err => err.msg || err.message)
        .filter(Boolean)
        .join('. ');
      if (validationErrors) {
        throw new Error(validationErrors);
      }
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Exercise and workout-related API functions
export const getExercises = async () => {
  try {
    const response = await defaultInstance.get('/exercises');
    return response.data;
  } catch (error) {
    console.error('Error fetching exercises:', error.message);
    return [];
  }
};

export const getExerciseDetail = async (id) => {
  try {
    const response = await defaultInstance.get(`/exercises/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching exercise ${id}:`, error.message);
    throw error;
  }
};

export const getWorkoutDetail = async (id) => {
  try {
    const response = await defaultInstance.get(`/workouts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching workout ${id}:`, error.message);
    throw error;
  }
};

export const saveWorkoutProgress = async (workoutId, exercisesCompleted, duration) => {
  try {
    logger.api('Saving workout progress:', { workoutId, exercisesCompleted, duration });
    const response = await defaultInstance.post('/progress/workout', {
      workoutId,
      exercisesCompleted,
      duration
    });
    logger.api('Workout progress saved:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error saving workout progress:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

export const toggleFavoriteExercise = async (exerciseId) => {
  try {
    const response = await defaultInstance.post(`/exercises/${exerciseId}/favorite`);
    return response.data;
  } catch (error) {
    console.error('Error toggling favorite exercise:', error.message);
    throw error;
  }
};

export const fetchRecommendedWorkouts = async () => {
  try {
    const response = await defaultInstance.get('/workouts/recommended');
    return response.data;
  } catch (error) {
    console.error('Error fetching recommended workouts:', error.message);
    return [];
  }
};

// Subscription-related API functions with improved error handling
export const getSubscription = async () => {
  try {
    const response = await defaultInstance.get('/stripe/subscription');
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription:', error.message);
    
    // Check if server is running/reachable
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error - Check your API connection');
      // Return a default subscription to avoid breaking the UI
      return { 
        hasSubscription: true, 
        tier: 'basic', 
        status: 'active',
        isFree: true,
        error: 'Could not connect to server'
      };
    }
    
    // For authentication errors, return a clean state
    if (error.response?.status === 401) {
      return {
        hasSubscription: false,
        tier: 'basic',
        error: 'Authentication error'
      };
    }
    
    // For other errors, throw to be handled by the calling function
    throw error;
  }
};

export const createCheckoutSession = async (data) => {
  try {
    const { tier, interval = 'month' } = data;
    const response = await defaultInstance.post('/stripe/create-checkout-session', { tier, interval });
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error.message);
    
    // Check for common errors and provide helpful messages
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Cannot connect to the server. Please check your internet connection and try again.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Unable to start checkout. Please try again.');
    } else if (error.response?.status === 401) {
      throw new Error('You must be logged in to upgrade your subscription');
    } else {
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMessage || error.message || 'Unable to start checkout. Please try again.');
    }
  }
};

export const upgradeSubscription = async (data) => {
  try {
    const { tier, interval = 'month', prorationBehavior = 'create_prorations' } = data;
    const response = await defaultInstance.post('/stripe/upgrade-subscription', { 
      tier, 
      interval, 
      prorationBehavior 
    });
    return response.data;
  } catch (error) {
    console.error('Error upgrading subscription:', error.message);
    
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Cannot connect to the server. Please try again later.');
    } else {
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      throw new Error(serverMessage || error.message || 'Failed to upgrade subscription');
    }
  }
};

export const upgradeFromBasic = async (data) => {
  try {
    const { tier, interval = 'month' } = data;
    const response = await defaultInstance.post('/stripe/upgrade-from-basic', { tier, interval });
    return response.data;
  } catch (error) {
    console.error('Error upgrading from basic tier:', error);
    console.error('Error response:', error.response?.data);
    
    // Extract detailed error message
    const errorMessage = error.response?.data?.error || 
                        error.response?.data?.message ||
                        error.message ||
                        'Failed to upgrade subscription';
    
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Cannot connect to the server. Please try again later.');
    } else {
      throw new Error(errorMessage);
    }
  }
};

export const downgradeToBasic = async () => {
  try {
    const response = await defaultInstance.post('/stripe/downgrade-to-basic');
    return response.data;
  } catch (error) {
    console.error('Error downgrading to basic tier:', error.message);
    throw error;
  }
};

export const immediateDowngradeToBasic = async () => {
  try {
    const response = await defaultInstance.post('/stripe/immediate-downgrade-to-basic');
    return response.data;
  } catch (error) {
    console.error('Error immediately downgrading to basic tier:', error.message);
    throw error;
  }
};

export const downgradeToPremium = async (interval = 'month') => {
  try {
    const response = await defaultInstance.post('/stripe/downgrade-to-premium', { interval });
    return response.data;
  } catch (error) {
    console.error('Error downgrading to premium tier:', error.message);
    throw error;
  }
};

export const reactivateSubscription = async () => {
  try {
    const response = await defaultInstance.post('/stripe/reactivate-subscription');
    return response.data;
  } catch (error) {
    console.error('Error reactivating subscription:', error.message);
    throw error;
  }
};

export const cancelSubscription = async () => {
  try {
    const response = await defaultInstance.post('/stripe/cancel-subscription');
    return response.data;
  } catch (error) {
    console.error('Error canceling subscription:', error.message);
    throw error;
  }
};

export const cancelSubscriptionImmediately = async () => {
  try {
    const response = await defaultInstance.post('/stripe/cancel-subscription-immediately');
    return response.data;
  } catch (error) {
    console.error('Error canceling subscription immediately:', error.message);
    throw error;
  }
};

export const signupBasic = async () => {
  try {
    const response = await defaultInstance.post('/stripe/signup-basic');
    return response.data;
  } catch (error) {
    console.error('Error signing up for basic tier:', error.message);
    throw error;
  }
};

export const calculateProration = async (data) => {
  try {
    const { tier, interval = 'month' } = data;
    const response = await defaultInstance.post('/stripe/calculate-proration', { tier, interval });
    return response.data;
  } catch (error) {
    console.error('Error calculating proration:', error.message);
    throw error;
  }
};

export const changeBillingCycle = async (interval) => {
  try {
    const response = await defaultInstance.post('/stripe/change-billing-cycle', { interval });
    return response.data;
  } catch (error) {
    console.error('Error changing billing cycle:', error.message);
    throw error;
  }
};

// User profile and preferences API functions
export const updateUserProfile = async (profileData) => {
  console.log('Updating user profile with data:', profileData);
  try {
    // First check if server is running
    const healthCheck = await checkServerHealth();
    console.log('Server health check:', healthCheck);
    
    if (healthCheck.status === 'error') {
      throw new Error('Server is not responding. Please try again later.');
    }

    // Use the correct endpoint that matches our server
    const response = await defaultInstance.put('/auth/me/profile', profileData);
    console.log('Profile update response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        data: error.config?.data
      }
    });
    throw error;
  }
};

export const updateUserPreferences = async (preferences) => {
  try {
    const response = await defaultInstance.put('/users/preferences', preferences);
    return response.data;
  } catch (error) {
    console.error('Error updating user preferences:', error.message);
    throw error;
  }
};

export const getUserActivityHistory = async () => {
  try {
    const response = await defaultInstance.get('/users/activity-history');
    return response.data;
  } catch (error) {
    console.error('Error fetching user activity history:', error.message);
    // Return empty array as fallback
    return [];
  }
};

// Payment-related API functions
export const getPaymentMethods = async () => {
  try {
    const response = await defaultInstance.get('/stripe/payment-methods');
    return response.data;
  } catch (error) {
    console.error('Error fetching payment methods:', error.message);
    // Return empty array as fallback
    return [];
  }
};

export const addPaymentMethod = async (paymentMethodId) => {
  try {
    const response = await defaultInstance.post('/stripe/payment-methods', { paymentMethodId });
    return response.data;
  } catch (error) {
    console.error('Error adding payment method:', error.message);
    throw error;
  }
};

export const updateDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    const response = await defaultInstance.put('/stripe/default-payment-method', { paymentMethodId });
    return response.data;
  } catch (error) {
    console.error('Error updating default payment method:', error.message);
    throw error;
  }
};

export const removePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await defaultInstance.delete(`/api/stripe/payment-methods/${paymentMethodId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing payment method:', error.message);
    throw error;
  }
};

export const getInvoices = async () => {
  try {
    const response = await defaultInstance.get('/stripe/invoices');
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error.message);
    // Return empty array as fallback
    return [];
  }
};

// Webhook verification - for debugging
export const checkWebhookStatus = async () => {
  try {
    const response = await defaultInstance.get('/stripe/test-webhook');
    return response.data;
  } catch (error) {
    console.error('Error checking webhook status:', error.message);
    return { status: 'error', message: error.message };
  }
};

// Newsletter / lead capture (public - no auth required)
export const subscribeNewsletter = async (email, source = 'sales-page') => {
  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail) {
    throw new Error('Email is required');
  }

  try {
    const response = await defaultInstance.post('/newsletter', {
      email: normalizedEmail,
      source
    });
    return response.data;
  } catch (error) {
    logger.error('Newsletter signup error:', error.message);
    // Surface a clean, user-friendly message
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const validationErrors = error.response.data.errors
        .map(err => err.msg || err.message)
        .filter(Boolean)
        .join('. ');
      if (validationErrors) {
        throw new Error(validationErrors);
      }
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Cannot connect to the server. Please try again.');
    }
    throw error;
  }
};

// Server health check
export const checkServerHealth = async () => {
  try {
    const response = await defaultInstance.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking server health:', error.message);
    return { status: 'error', message: error.message };
  }
};

// Device-related API functions
export const getDevices = async () => {
  try {
    const response = await defaultInstance.get('/devices');
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error.message);
    return [];
  }
};

export const scanForDevices = async () => {
  try {
    const response = await defaultInstance.post('/devices/scan');
    return response.data;
  } catch (error) {
    console.error('Error scanning for devices:', error.message);
    throw error;
  }
};


// Progress-related API functions
export const getProgressData = async () => {
  try {
    logger.api('Fetching progress data...');
    const response = await defaultInstance.get('/progress');
    logger.api('Progress data response:', response.data);
    
    // Validate the response data
    if (!response.data || !hasValidWorkoutData(response.data)) {
      logger.debug('No valid workout data received, returning empty state');
      return {
        workouts: [],
        streak: 0,
        lastWorkout: null,
        totalWorkouts: 0,
        workoutHistory: [],
        achievements: []
      };
    }
    
    // Filter and validate the data
    const validWorkouts = response.data.workouts?.filter(w => 
      w.completedAt && w.exercisesCompleted?.length > 0
    ) || [];
    
    const validHistory = response.data.workoutHistory?.filter(w => 
      w.completedAt && w.exercisesCompleted?.length > 0
    ) || [];
    
    return {
      workouts: validWorkouts,
      streak: response.data.streak > 0 && response.data.lastWorkout ? response.data.streak : 0,
      lastWorkout: response.data.lastWorkout || null,
      totalWorkouts: validHistory.length,
      workoutHistory: validHistory,
      achievements: response.data.achievements || []
    };
  } catch (error) {
    console.error('Error fetching progress data:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    // Return empty state on error
    return {
      workouts: [],
      streak: 0,
      lastWorkout: null,
      totalWorkouts: 0,
      workoutHistory: [],
      achievements: []
    };
  }
};

export const getWorkoutHistory = async () => {
  try {
    logger.api('Fetching workout history...');
    const response = await defaultInstance.get('/progress/history');
    logger.api('Workout history response:', response.data);
    
    // Validate the response data
    if (!response.data || !response.data.history || !Array.isArray(response.data.history)) {
      logger.debug('No valid workout history received, returning empty state');
      return {
        history: [],
        totalWorkouts: 0
      };
    }
    
    // Filter and validate the history
    const validHistory = response.data.history.filter(w => 
      w.completedAt && w.exercisesCompleted?.length > 0
    );
    
    return {
      history: validHistory,
      totalWorkouts: validHistory.length
    };
  } catch (error) {
    console.error('Error fetching workout history:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    // Return empty state on error
    return {
      history: [],
      totalWorkouts: 0
    };
  }
};

// Helper function to validate workout data
const hasValidWorkoutData = (data) => {
  if (!data) return false;
  
  // Check if workouts array has valid entries
  const hasValidWorkouts = Array.isArray(data.workouts) && 
    data.workouts.length > 0 &&
    data.workouts.some(w => w.completedAt && w.exercisesCompleted?.length > 0);

  // Check if workout history has valid entries
  const hasValidHistory = Array.isArray(data.workoutHistory) && 
    data.workoutHistory.length > 0 &&
    data.workoutHistory.some(w => w.completedAt && w.exercisesCompleted?.length > 0);

  // Check if streak and totalWorkouts are backed by real data
  const hasValidStats = (
    (data.streak > 0 && data.lastWorkout) ||
    (data.totalWorkouts > 0 && hasValidHistory)
  );

  return hasValidWorkouts || hasValidHistory || hasValidStats;
};

export const getHealthData = async () => {
  try {
    const response = await defaultInstance.get('/health/data');
    return response.data;
  } catch (error) {
    console.error('Error in getHealthData:', error);
    throw error;
  }
};

// Export the axios instance for direct use
export { defaultInstance as api };